import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Contract, getAllPersonalContractsForVessel, getContract } from '@store/contract/contractSlice';
import { getAllPositions, selectPosition } from '@store/masterData/positionSlice';
import { getAllWorkers, selectWorker } from '@store/masterData/workerSlice';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { AssignmentStatusesClient, AssignmentStatusResponse, DuplicateCrewRequest, TripCrewHistoryRosterResponse, TripCrewRequest, TripCrewResponse, TripCrewsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { dateContains } from 'src/utility/helper/dateHelper';
import { Equal, handleError, showNotFoundErrorNotification, showWarningNotification } from 'src/utility/utils';
import { CrewDataModel } from 'src/views/fishingTrip/Import/DataTypes';
import { getTripPeriod } from './tripPeriodSlice';

export interface TripCrew extends TripCrewResponse { }

export interface TripCrewHistoryRoster extends TripCrewHistoryRosterResponse {
  key: number;
  newPositionCode: string;
  newAssignment: string;
  newIsOnBoard: boolean;
  newShare: number;
  newCostShare: number;
  selected: boolean;
  isOnBoard: boolean;
  workerPositionSortOrder: number;
}

export interface State {
  isLoading: boolean;
  error: boolean;
  tripCrewList: TripCrew[];
  assignmentStatusesList: AssignmentStatusResponse[];

  isTripCrewHistoryRosterListLoading: boolean;
  tripCrewHistoryRosterList: TripCrewHistoryRoster[];
}

const initialState: State = {
  isLoading: false,
  error: false,
  tripCrewList: [],
  assignmentStatusesList: [],

  isTripCrewHistoryRosterListLoading: false,
  tripCrewHistoryRosterList: [],
};

const tripCrewSlice = createSlice({
  name: 'tripCrew',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.isTripCrewHistoryRosterListLoading = false;
      state.error = action.payload;
      state.isLoading = false;
    },
    getTripCrewListSuccess: (state, action) => {
      state.tripCrewList = action.payload;
      state.isLoading = false;
    },
    startTripCrewHistoryRosterListLoading: (state) => {
      state.isTripCrewHistoryRosterListLoading = true;
    },
    getTripCrewHistoryRosterSuccess: (state, action) => {
      state.tripCrewHistoryRosterList = action.payload;
      state.isTripCrewHistoryRosterListLoading = false;
    },
    addNewTripCrewSuccess: (state, action: PayloadAction<TripCrew>) => {
      const newItem = action.payload;
      state.tripCrewList = [newItem, ...state.tripCrewList];
      toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
      state.isLoading = false;
    },
    updateTripCrewSuccess: (state, action: PayloadAction<TripCrew>) => {
      const updatedItem = action.payload;
      const index = state.tripCrewList.findIndex(item => item.tripCrewId === updatedItem.tripCrewId);
      state.tripCrewList = [...state.tripCrewList.slice(0, index), updatedItem, ...state.tripCrewList.slice(index + 1)];
      state.isLoading = false;
    },
    deleteTripCrewSuccess: (state, action: PayloadAction<number>) => {
      const deleteId = action.payload;
      state.tripCrewList = state.tripCrewList.filter(item => item.tripCrewId !== deleteId);
      state.isLoading = false;
    },
    getAssignmentStatusesListSuccess: (state, action) => {
      state.assignmentStatusesList = action.payload;
      state.isLoading = false;
    },
    bulkEditCrewAllPeriodsSuccess: (state, action) => {
      state.tripCrewList = state.tripCrewList.filter(item =>
        !action.payload.some(payloadItem => payloadItem.tripCrewId === item.tripCrewId
          && payloadItem.fishingTripPeriodId === item.fishingTripPeriodId
          && payloadItem.isOnBoard === item.isOnBoard)).concat(action.payload);
      state.isLoading = false;
    },
  },
});

// Reducer
export default tripCrewSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
  getTripCrewListSuccess,
} = tripCrewSlice.actions;

// for useSelector
export const selectTripCrew = (state: RootState) => state.tripCrew as State;

// ----------------------------------------------------------------------
export function getAllTripCrew(fishingTripId: number) {
  return async (dispatch, getState) => {
    dispatch(tripCrewSlice.actions.startLoading());
    try {
      const client = new TripCrewsClient(undefined, axios);
      const response = await client.getByFishingTrip(fishingTripId);
      dispatch(tripCrewSlice.actions.getTripCrewListSuccess(response));
      return response;
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createTripCrew(tripCrew: TripCrew) {
  return async (dispatch, getState) => {
    dispatch(tripCrewSlice.actions.startLoading());
    try {
      const { tripCrewId, ...payload } = tripCrew;
      const request = payload as TripCrewRequest;
      const client = new TripCrewsClient(undefined, axios);
      const response = await client.create(request);
      dispatch(tripCrewSlice.actions.addNewTripCrewSuccess(response));
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function updateTripCrew(tripCrew: TripCrew, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(tripCrewSlice.actions.startLoading());
    try {
      const { tripCrewId, ...payload } = tripCrew;
      const request = payload as TripCrewRequest;
      const client = new TripCrewsClient(undefined, axios);
      const response = await client.edit(tripCrewId, request);
      dispatch(tripCrewSlice.actions.updateTripCrewSuccess(response));
      if (showNotification) {
        toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
      }
      return true;
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
      return false;
    }
  };
}

export function deleteTripCrew(tripCrewId: number, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(tripCrewSlice.actions.startLoading());
    try {
      const client = new TripCrewsClient(undefined, axios);
      await client.delete(tripCrewId);
      dispatch(tripCrewSlice.actions.deleteTripCrewSuccess(tripCrewId));
      if (showNotification) {
        toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
      }
      return true;
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
      return false;
    }
  };
}

export function bulkEditCrewAllPeriods(fishingTripId: number, tripCrew: TripCrew) {
  return async (dispatch, getState) => {
    dispatch(tripCrewSlice.actions.startLoading());
    try {
      const client = new TripCrewsClient(undefined, axios);
      const response = await client.bulkEditCrewAllPeriods(tripCrew, fishingTripId);
      dispatch(tripCrewSlice.actions.bulkEditCrewAllPeriodsSuccess(response));
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function duplicateTripCrew(duplicateCrew: DuplicateCrewRequest) {
  return async (dispatch, getState) => {
    try {
      const client = new TripCrewsClient(undefined, axios);
      await client.duplicateCrew(duplicateCrew);
      toast.success(stringResources.REDUX_SLICE.DUPLICATE_SUCCESS);
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function getAllAssignmentStatuses(ignoreCache: boolean = false) {
  return async (dispatch, getState) => {
    try {
      const { assignmentStatusesList } = selectTripCrew(getState());
      if (ignoreCache || assignmentStatusesList.length === 0) {
        dispatch(tripCrewSlice.actions.startLoading());
        const client = new AssignmentStatusesClient(undefined, axios);
        const response = await client.getAll();
        dispatch(tripCrewSlice.actions.getAssignmentStatusesListSuccess(response));
      }
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createCrewCatchBatch(data: CrewDataModel[], periodIdMap: Map<string, number>) {
  return async (dispatch, getState) => {
    try {
      await dispatch(getAllWorkers());
      await dispatch(getAllPositions());
      await dispatch(getAllAssignmentStatuses());

      const { workerList } = selectWorker(getState());
      const { positionList } = selectPosition(getState());
      const { assignmentStatusesList } = selectTripCrew(getState());

      const request: TripCrewRequest[] = [];
      const notFoundItems: string[] = [];
      for (const d of data) {
        const fishingTripPeriodId = periodIdMap.get(d.Period ?? '');
        const workerId = workerList.find(w => w.externalId === d.PGId)?.workerId;
        if (!workerId) {
          notFoundItems.push(d.PGId ?? 'null');
          continue;
        }

        const positionId = positionList.find(p => Equal(p.positionCode, d.PositionCode))?.positionId;
        if (!positionId) {
          notFoundItems.push(d.PositionCode ?? 'null');
          continue;
        }

        const assignmentStatusId = assignmentStatusesList.find(a => a.statusName.toLowerCase() === d.Assignment?.toLowerCase().trim())?.assignmentStatusId;
        if (!assignmentStatusId) {
          notFoundItems.push(d.Assignment ?? 'null');
          continue;
        }

        if (fishingTripPeriodId) {
          request.push({
            fishingTripPeriodId,
            workerId,
            positionId,
            isOnBoard: d.IsOnBoard,
            assignmentStatusId: assignmentStatusId,
            share: d.Share,
            costShare: d.CostShare,
            dailyRate: d.DailyRate,
            interimDailyRate: d.InterimDailyRate,
            interimPayPercentage: d.InterimPayPercentage
          } as TripCrewRequest);
        }
      }
      const client = new TripCrewsClient(undefined, axios);
      await client.createBatch(request);
      if (notFoundItems.length > 0) {
        showNotFoundErrorNotification(notFoundItems.join(','));
      }
    } catch (error) {
      handleError(error);
    }
  };
}

export function createCrewCatchBatchFromRequest(request: TripCrewRequest[], fishingTripId: number) {
  return async (dispatch, getState) => {
    try {
      const client = new TripCrewsClient(undefined, axios);
      await client.createBatch(request);
      await dispatch(getAllTripCrew(fishingTripId));
    } catch (error) {
      handleError(error);
    }
  };
}

export function getTripCrewHistory(vesselId: number, startDate: Date, callback: Function) {
  return async (dispatch, getState) => {
    dispatch(tripCrewSlice.actions.startTripCrewHistoryRosterListLoading());
    try {
      const client = new TripCrewsClient(undefined, axios);
      // I have no idea why the startDate is not a date, but without next line it just doesn't work #IHateJavascript
      var date = new Date(startDate);
      const response = await client.getTripCrewHistoryRoster(vesselId, date);
      dispatch(tripCrewSlice.actions.getTripCrewHistoryRosterSuccess(response));
      callback();
      return response;
    } catch (error) {
      dispatch(tripCrewSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createCrewCatchBatchFromRoster(fishingTripPeriodId: number, vesselId: number, vesselContractId: number, dataList: TripCrewHistoryRoster[]) {
  return async (dispatch, getState) => {
    try {
      await dispatch(getAllWorkers());
      await dispatch(getAllPositions());
      await dispatch(getAllAssignmentStatuses());

      const { workerList } = selectWorker(getState());
      const { positionList } = selectPosition(getState());
      const { assignmentStatusesList } = selectTripCrew(getState());

      const personalContractList = await getAllPersonalContractsForVessel(vesselId);
      const vesselContract = await dispatch(getContract(vesselContractId));

      const request: TripCrewRequest[] = [];
      const notFoundItems: string[] = [];
      for (const d of dataList) {
        const workerId = workerList.find(w => w.externalId === d.externalId)?.workerId;
        if (!workerId) {
          notFoundItems.push(d.externalId ?? 'null');
          continue;
        }

        const positionId = positionList.find(p => Equal(p.positionCode, d.newPositionCode))?.positionId;
        if (!positionId) {
          notFoundItems.push(d.positionCode ?? 'null');
          continue;
        }

        const assignmentStatusId = assignmentStatusesList.find(a => Equal(a.statusName, d.newAssignment))?.assignmentStatusId;
        if (!assignmentStatusId) {
          notFoundItems.push(d.assignment ?? 'null');
          continue;
        }

        if (fishingTripPeriodId) {
          const { fishingTripPeriodStartDate, fishingTripPeriodEndDate } = await getTripPeriod(fishingTripPeriodId);
          const { dailyRate, interimDailyRate, interimPayPercentage } =
            fetchDefaultValuesOnContract(personalContractList, vesselContract, workerId, positionId, false, fishingTripPeriodStartDate, fishingTripPeriodEndDate);

          request.push({
            fishingTripPeriodId,
            workerId,
            positionId,
            assignmentStatusId: assignmentStatusId,
            isOnBoard: d.newIsOnBoard,
            share: d.newShare,
            costShare: d.newCostShare,
            dailyRate: dailyRate ?? 0,
            interimDailyRate: interimDailyRate ?? 0,
            interimPayPercentage: interimPayPercentage ?? 1
          } as TripCrewRequest);
        }
      }
      const client = new TripCrewsClient(undefined, axios);
      await client.createBatch(request);
      if (notFoundItems.length > 0) {
        showNotFoundErrorNotification(notFoundItems.join(','));
      }
    } catch (error) {
      handleError(error);
    }
  };
}

export function fetchDefaultValuesOnContract(personalContractList: Contract[], vesselContract: Contract | undefined,
  workerId: number, positionId: number, showWarning: boolean, fishingTripPeriodStartDate: Date | null, fishingTripPeriodEndDate: Date | null) {

  const personalContracts = personalContractList
    .filter(c => c.workerId === workerId && !c.isDeleted)
    .sort((a, b) => DateTime.fromJSDate(b.validToDate).diff(DateTime.fromJSDate(a.validToDate)).as('milliseconds'));;

  const validPersonalContracts = personalContracts.filter(c =>
    dateContains(c.validFromDate, c.validToDate, fishingTripPeriodStartDate)
    && dateContains(c.validFromDate, c.validToDate, fishingTripPeriodEndDate));

  const personalContract = validPersonalContracts.length > 0 ? validPersonalContracts[0] : personalContracts[0];
  
  let contractShare = (personalContract?.contractShare ?? []).find(share => share.positionId === positionId);
  if (!contractShare) {
    contractShare = (vesselContract?.contractShare ?? []).find(share => share.positionId === positionId);
  }

  let share: null | number = null;
  let costShare: null | number = null;
  if (contractShare) {
    share = contractShare.share;
    costShare = contractShare.costShare;
  } else {
    if (showWarning) {
      showWarningNotification(stringResources.NO_DEFAULT_WARNING.SHARE);
    }
  }

  let contractRate = (personalContract?.contractRate ?? []).find(rate => rate.positionId === positionId);
  if (!contractRate) {
    contractRate = (vesselContract?.contractRate ?? []).find(rate => rate.positionId === positionId);
  }

  let dailyRate: null | number = null;
  let interimDailyRate: null | number = null;
  let interimPayPercentage: null | number = null;
  if (contractRate) {
    dailyRate = contractRate.dailyRate;
    interimDailyRate = contractRate.interimDailyRate;
    interimPayPercentage = contractRate.interimPayPercentage;
  } else {
    if (showWarning) {
      showWarningNotification(stringResources.NO_DEFAULT_WARNING.RATE);
    }
  }
  return { share, costShare, dailyRate, interimDailyRate, interimPayPercentage }
}